// src/components/AddonsBadge.jsx
import React, { useCallback, useMemo } from 'react';
import { FlaskConical, Mail, Printer } from 'lucide-react';
import { getDisplayMusiqId } from '../utils/musiqId';

export default function AddonsBadge({
  bio = 'non',
  onChangeBio = () => {},
  disabledBio = false,
  patient = null,
  onPrintClick = null,
  padEmail = null,
}) {
  const mailtoUrl = useMemo(() => {
    if (!patient) return null;
    const to = padEmail || 'secretaire001@monurgencesante.fr';
    const subj = `Envoi de biologie requis passage ${getDisplayMusiqId(patient)}`;
    const body = `Veuillez svp envoyer au biologiste les informations pour le patient ${patient?.prenom || ''} ${patient?.nom || ''}.`;
    return `mailto:${to}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(body)}`;
  }, [patient, padEmail]);

  const openMail = useCallback((url) => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.style.position = 'absolute';
    a.style.left = '-9999px';
    a.setAttribute('rel', 'noopener');
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      try {
        document.body.removeChild(a);
      } catch {}
    }, 0);
  }, []);

  const onPadClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      openMail(mailtoUrl);
    },
    [mailtoUrl, openMail],
  );

  const MiniSwitch = ({ checked, onChange, disabled }) => (
    <button
      type="button"
      aria-pressed={checked}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
      className={[
        'relative h-5 w-9 rounded-full transition',
        checked ? 'bg-emerald-500' : 'bg-slate-300',
        disabled ? 'cursor-not-allowed opacity-50' : 'hover:opacity-95',
      ].join(' ')}
    >
      <span
        className={[
          'absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition',
          checked ? 'right-0.5' : 'left-0.5',
        ].join(' ')}
      />
    </button>
  );

  return (
    <div
      className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:items-center rounded-none border-0 sm:rounded-full sm:border sm:border-slate-200 bg-white/90 px-2 py-1 shadow-sm"
      onClick={(e) => e.stopPropagation()} // évite que le parent considère toute la pastille comme cliquable
    >
      {/* Biologie */}
      <div className="flex items-center gap-1">
        <FlaskConical
          className={['h-4 w-4', bio !== 'non' ? 'text-emerald-600' : 'text-slate-400'].join(' ')}
        />
        <select
          value={bio}
          onChange={(e) => onChangeBio(e.target.value)}
          disabled={disabledBio}
          className="text-sm border rounded px-2 py-1"
        >
          <option value="non">Non</option>
          <option value="urine interne">Urine | Interne</option>
          <option value="urine externe">Urine | Externe</option>
          <option value="sang interne externe">Sang | Interne+Externe</option>
          <option value="sang urine interne externe">Sang+Urine | Interne+Externe</option>
        </select>
      </div>

      <div className="mx-1 h-4 w-px bg-slate-200" />

      {/* PAD */}
      <button
        type="button"
        aria-label="Envoi biologie"
        title="Envoi biologie"
        aria-disabled={!mailtoUrl}
        disabled={!mailtoUrl}
        className={[
          'relative z-10 flex items-center gap-1 px-2 py-1 rounded border bg-white/90',
          'border-slate-300 text-slate-700 hover:bg-white pointer-events-auto',
          !mailtoUrl ? 'opacity-50 cursor-not-allowed' : '',
        ].join(' ')}
        onClick={onPadClick}
        onTouchStart={(e) => e.stopPropagation()} // mobile : coupe le bubble sans empêcher le click
      >
        <Mail className="h-4 w-4" />
        <span className="text-sm">Labo</span>
      </button>

      {/* Étiquettes biologie (à la place de Radio) */}
      <button
        type="button"
        aria-label="Imprimer les étiquettes de biologie"
        title="Imprimer les étiquettes de biologie"
        disabled={!onPrintClick || !patient}
        className={[
          'relative z-10 flex items-center gap-1 px-2 py-1 rounded border bg-white/90',
          'border-red-300 text-red-700 hover:bg-red-50 pointer-events-auto',
          !onPrintClick || !patient ? 'opacity-50 cursor-not-allowed' : '',
        ].join(' ')}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onPrintClick) onPrintClick();
        }}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <Printer className="h-4 w-4" />
      </button>
    </div>
  );
}
